document.addEventListener('DOMContentLoaded', () => {

    const section = document.querySelector('section.event_tickets');
    if (!section) return;

    // -------------------------------------------------------------------------
    // Membership lock
    // -------------------------------------------------------------------------
    const membershipLock = section.querySelector('.membership_lock');

    if (membershipLock) {
        const form = membershipLock.querySelector('form.membership_form');
        const input = membershipLock.querySelector('input[name="member_id"]');
        const submitBtn = membershipLock.querySelector('button[type="submit"]');

        form.addEventListener('submit', async (e) => {
            e.preventDefault();

            const memberId = input.value.trim();

            if (!memberId || !/^\d+$/.test(memberId) || parseInt(memberId, 10) <= 0) {
                window.setFieldError(input, 'Please enter a valid membership number.');
                return;
            }

            window.clearFieldError(input);
            submitBtn.disabled = true;
            const originalText = submitBtn.textContent;
            submitBtn.textContent = 'Checking\u2026';

            const result = await window.validateMemberNumber(memberId);

            if (result.verified) {
                membershipLock.remove();
                return;
            }

            submitBtn.disabled = false;
            submitBtn.textContent = originalText;

            if (result.status === 429) {
                window.setFieldError(input, 'Too many attempts. Please try again shortly.');
            } else if (result.status === 404) {
                window.setFieldError(input, 'Membership number not found.');
            } else {
                window.setFieldError(input, 'Something went wrong. Please try again.');
            }
        });
    }

    // -------------------------------------------------------------------------
    // DOM references
    // -------------------------------------------------------------------------
    const ticketList     = section.querySelector('.ticket_list');
    const summaryLines   = section.querySelector('.order_summary_lines');
    const summaryTotal   = section.querySelector('.order_summary_total strong');
    const eventDateInput = section.querySelector('input[name=event_date_id], select[name=event_date_id]');
    const eventIdInput   = document.querySelector('input[name=event_id]');

    if (!eventDateInput || !eventIdInput) return;

    const fmt      = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' });
    const fmtPrice = (value) => { const n = parseFloat(value); return (isNaN(n) || n === 0) ? 'Free' : fmt.format(n); };

    // Fallback max per ticket type used when the API value is absent.
    const DEFAULT_MAX_QTY = 5;

    // -------------------------------------------------------------------------
    // Fetch
    // -------------------------------------------------------------------------
    const fetchTickets = async (eventId, eventDateId) => {
        if (!/^\d+$/.test(String(eventDateId)) || parseInt(eventDateId, 10) <= 0) {
            return { data: null, status: 422 };
        }

        try {
            const response = await fetch('/json/events/event-tickets', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-CSRF-TOKEN': window.csrfToken(),
                },
                body: JSON.stringify({ event_id: String(eventId), event_date_id: String(eventDateId) }),
            });

            return { data: await response.json() ?? null, status: response.status };
        } catch {
            return { data: null, status: null };
        }
    };

    // -------------------------------------------------------------------------
    // Render
    // -------------------------------------------------------------------------
    const renderTicketItem = (ticket) => {
        const item = document.createElement('div');
        item.className = 'ticket_item';
        item.dataset.ticketId = ticket.id;
        item.dataset.price = ticket.price;
        // People per ticket: each ticket sold admits this many people (one QR
        // each), so it consumes this much capacity - unless the type does not
        // use event capacity, in which case it consumes none.
        item.dataset.capacityWeight = ticket.capacity_weight ?? 1;
        item.dataset.noCapacity = ticket.no_capacity_impact ? '1' : '0';

        const info = document.createElement('div');
        info.className = 'ticket_info';

        const text = document.createElement('div');
        text.className = 'ticket_text';

        const heading = document.createElement('h3');
        heading.textContent = ticket.name;
        text.appendChild(heading);

        if (ticket.abstract) {
            const abstract = document.createElement('p');
            abstract.textContent = ticket.abstract;
            text.appendChild(abstract);
        }

        const price = document.createElement('p');
        price.className = 'ticket_price';
        price.textContent = fmtPrice(ticket.price);

        info.append(text, price);

        const quantityWrapper = document.createElement('div');
        quantityWrapper.className = 'ticket_quantity';

        if (ticket.available) {
            const stepper = document.createElement('div');
            stepper.className = 'qty_stepper';

            const decBtn = document.createElement('button');
            decBtn.type = 'button';
            decBtn.className = 'qty_btn';
            decBtn.dataset.action = 'decrement';
            decBtn.textContent = '\u2212';
            decBtn.disabled = true;

            const qtyInput = document.createElement('input');
            qtyInput.type = 'number';
            qtyInput.className = 'qty_value';
            qtyInput.name = 'items[' + ticket.id + ']';
            const maxPerCustomer = ticket.max_per_customer ?? DEFAULT_MAX_QTY;
            // Per-type cap headroom: null/absent means uncapped. Unlike capacity
            // it is a static ceiling for this type only — it never shifts as
            // other steppers change.
            const capRemaining = ticket.cap_remaining ?? null;
            const maxQty = ticket.max_available ?? maxPerCustomer;

            qtyInput.value = '0';
            qtyInput.min = '0';
            qtyInput.max = String(maxQty);
            qtyInput.dataset.max = maxQty;
            qtyInput.dataset.maxPerCustomer = maxPerCustomer;
            if (capRemaining !== null) qtyInput.dataset.capRemaining = capRemaining;

            const incBtn = document.createElement('button');
            incBtn.type = 'button';
            incBtn.className = 'qty_btn';
            incBtn.dataset.action = 'increment';
            incBtn.textContent = '+';

            stepper.append(decBtn, qtyInput, incBtn);
            quantityWrapper.appendChild(stepper);
        } else {
            quantityWrapper.classList.add('ticket_quantity--sold-out');
            const soldOut = document.createElement('p');
            soldOut.className = 'ticket_sold_out';
            soldOut.textContent = 'Sold out';
            quantityWrapper.appendChild(soldOut);
        }

        item.append(info, quantityWrapper);
        return item;
    };

    const updateOrderSummary = () => {
        const lines = [];
        let total = 0;

        section.querySelectorAll('.ticket_item').forEach((item) => {
            const qtyInput = item.querySelector('input.qty_value');
            if (!qtyInput) return;
            const qty = parseInt(qtyInput.value, 10);
            if (qty <= 0) return;

            const ticketPrice = parseFloat(item.dataset.price) || 0;
            const name        = item.querySelector('h3').textContent;
            const subtotal    = qty * ticketPrice;
            total += subtotal;

            const nameSpan = document.createElement('span');
            nameSpan.textContent = name + ' \u00d7 ' + qty;

            const priceSpan = document.createElement('span');
            priceSpan.textContent = fmtPrice(subtotal);

            const line = document.createElement('div');
            line.className = 'order_summary_line';
            line.append(nameSpan, priceSpan);
            lines.push(line);
        });

        if (lines.length) {
            summaryLines.replaceChildren(...lines);
        } else {
            const empty = document.createElement('p');
            empty.className = 'order_summary_empty';
            empty.textContent = 'Select your tickets.';
            summaryLines.replaceChildren(empty);
        }

        // Only say "Free" when tickets are actually selected and they total zero;
        // an empty basket shows a money zero, not "Free".
        summaryTotal.textContent = lines.length ? fmtPrice(total) : fmt.format(0);
    };

    const renderTicketList = (tickets, remaining) => {
        ticketList.replaceChildren();

        if (remaining !== undefined && remaining !== null) {
            ticketList.dataset.remaining = remaining;
        } else {
            delete ticketList.dataset.remaining;
        }

        if (!tickets.length) {
            const msg = document.createElement('p');
            msg.textContent = 'No tickets are available for this date.';
            ticketList.appendChild(msg);
            updateOrderSummary();
            return;
        }

        tickets.forEach((ticket) => ticketList.appendChild(renderTicketItem(ticket)));
        recalculateMaxima();
        updateOrderSummary();
    };

    // -------------------------------------------------------------------------
    // Quantity stepper — delegated listeners on the list container
    // -------------------------------------------------------------------------
    const syncStepperButtons = (stepper, qty) => {
        const max = parseInt(stepper.querySelector('input.qty_value').dataset.max, 10);
        stepper.querySelector('[data-action="decrement"]').disabled = qty === 0;
        stepper.querySelector('[data-action="increment"]').disabled = qty >= max;
    };

    const recalculateMaxima = () => {
        const remainingRaw = ticketList.dataset.remaining;
        if (remainingRaw === undefined) return; // unlimited capacity

        const remainingTotal = parseInt(remainingRaw, 10);

        let consumed = 0;
        section.querySelectorAll('.ticket_item').forEach((item) => {
            const qty    = parseInt(item.querySelector('input.qty_value')?.value ?? 0, 10);
            const weight = parseInt(item.dataset.capacityWeight ?? 1, 10);
            const noCap  = item.dataset.noCapacity === '1';
            // Each ticket admits `weight` people; a no-capacity type eats none.
            consumed += noCap ? 0 : qty * weight;
        });

        const effectiveRemaining = remainingTotal - consumed;

        section.querySelectorAll('.ticket_item').forEach((item) => {
            const qtyInput = item.querySelector('input.qty_value');
            if (!qtyInput) return;

            const currentQty  = parseInt(qtyInput.value, 10);
            const weight      = parseInt(item.dataset.capacityWeight ?? 1, 10);
            const noCap       = item.dataset.noCapacity === '1';
            const perCustomer = parseInt(qtyInput.dataset.maxPerCustomer, 10);
            const capRaw      = qtyInput.dataset.capRemaining;
            const capRemaining = capRaw === undefined ? null : parseInt(capRaw, 10);

            let newMax;
            if (noCap) {
                // Does not use event capacity, so only the per-customer limit binds here.
                newMax = perCustomer;
            } else {
                // Add back this ticket's own contribution so it can still increase its own count.
                // weight = people per ticket, so headroom is measured in whole tickets.
                const availableForThis = effectiveRemaining + (currentQty * weight);
                newMax = Math.min(perCustomer, Math.floor(availableForThis / weight));
            }

            // Clamp to this type's own cap headroom (static, not shared with other types).
            if (capRemaining !== null) newMax = Math.min(newMax, capRemaining);

            qtyInput.dataset.max = newMax;
            qtyInput.max         = String(newMax);
            syncStepperButtons(qtyInput.closest('.qty_stepper'), currentQty);
        });
    };

    // Stepper buttons
    ticketList.addEventListener('click', (e) => {
        const btn = e.target.closest('.qty_btn');
        if (!btn) return;

        const stepper  = btn.closest('.qty_stepper');
        const qtyInput = stepper.querySelector('input.qty_value');
        const max      = parseInt(qtyInput.dataset.max, 10);
        let qty = parseInt(qtyInput.value, 10);

        if (btn.dataset.action === 'increment') {
            qty = Math.min(qty + 1, max);
        } else if (btn.dataset.action === 'decrement') {
            qty = Math.max(qty - 1, 0);
        }

        qtyInput.value = qty;
        syncStepperButtons(stepper, qty);
        recalculateMaxima();
        updateOrderSummary();
    });

    // Manual input — clamp if over max, allow empty while mid-type, update summary live
    ticketList.addEventListener('input', (e) => {
        const input = e.target.closest('input.qty_value');
        if (!input) return;

        const max = parseInt(input.dataset.max, 10);
        const raw = parseInt(input.value, 10);
        const qty = isNaN(raw) ? 0 : Math.min(Math.max(raw, 0), max);

        if (!isNaN(raw)) input.value = qty; // clamp without interrupting an empty field
        syncStepperButtons(input.closest('.qty_stepper'), qty);
        recalculateMaxima();
        updateOrderSummary();
    });

    // Normalise on blur/enter — catches an empty field left behind
    ticketList.addEventListener('change', (e) => {
        const input = e.target.closest('input.qty_value');
        if (!input) return;

        const max = parseInt(input.dataset.max, 10);
        const raw = parseInt(input.value, 10);
        const qty = isNaN(raw) ? 0 : Math.min(Math.max(raw, 0), max);

        input.value = qty;
        syncStepperButtons(input.closest('.qty_stepper'), qty);
        recalculateMaxima();
        updateOrderSummary();
    });

    // -------------------------------------------------------------------------
    // Load — used on page load and on date change
    // -------------------------------------------------------------------------
    const showLoader = () => {
        const loader = document.createElement('div');
        loader.className = 'ticket_list_loader';
        ticketList.replaceChildren(loader);
    };

    const loadTickets = async (eventId, eventDateId) => {
        // Snapshot height before replacing content so existing space is preserved.
        // On initial load (empty list) height is 0, so we skip locking and let
        // the loader render at its natural height instead.
        const currentHeight = ticketList.offsetHeight;
        showLoader();

        if (currentHeight > 0) {
            ticketList.style.height = currentHeight + 'px';
        }

        ticketList.classList.add('is-loading');

        const { data } = await fetchTickets(eventId, eventDateId);

        ticketList.classList.remove('is-loading');

        if (data?.tickets) {
            renderTicketList(data.tickets, data.remaining ?? null);
        } else {
            ticketList.replaceChildren();
        }

        // Animate from the locked/current height to the new content height.
        // Reading style.height gives the locked px value (or '' on initial load),
        // falling back to offsetHeight so both cases produce a numeric from value.
        const fromHeight = parseInt(ticketList.style.height) || ticketList.offsetHeight;
        ticketList.style.height = 'auto';
        const toHeight = ticketList.offsetHeight;
        ticketList.style.height = fromHeight + 'px';
        void ticketList.offsetHeight; // force reflow so transition sees the from value
        ticketList.style.height = toHeight + 'px';

        const clearHeight = () => { ticketList.style.height = ''; };
        if (fromHeight === toHeight) {
            clearHeight();
        } else {
            ticketList.addEventListener('transitionend', clearHeight, { once: true });
        }
    };

    const ticketsFormDateInput = section.querySelector('#event_tickets_form input[name="event_date_id"]');

    eventDateInput.addEventListener('change', (e) => {
        loadTickets(eventIdInput.value, e.target.value);
        if (ticketsFormDateInput) ticketsFormDateInput.value = e.target.value;

        // Keep the URL in sync so a refresh reloads the currently-selected date
        // (EventsController::viewEvent reads event_date_id from the query string).
        if (typeof history.replaceState === 'function') {
            const url = new URL(window.location.href);
            url.searchParams.set('event_date_id', e.target.value);
            history.replaceState(null, '', url);
        }
    });

    loadTickets(eventIdInput.value, eventDateInput.value);

    // -------------------------------------------------------------------------
    // Basket submission
    // -------------------------------------------------------------------------
    const ticketsForm   = section.querySelector('#event_tickets_form');
    const submitBtn     = ticketsForm?.querySelector('button[type="submit"]');
    const checkoutError = section.querySelector('.checkout_error');
    const defaultSubmitText = submitBtn?.textContent;

    if (! ticketsForm || ! submitBtn) return;

    const setCheckoutError = (msg) => {
        if (! checkoutError) return;
        checkoutError.textContent = msg ?? '';
        checkoutError.hidden = ! msg;
    };

    const getSelectedItems = () =>
        [...section.querySelectorAll('.ticket_item')].reduce((acc, item) => {
            const input = item.querySelector('input.qty_value');
            if (! input) return acc;
            const qty = parseInt(input.value, 10);
            if (qty > 0) acc.push({ event_date_ticket_id: parseInt(item.dataset.ticketId, 10), quantity: qty });
            return acc;
        }, []);

    const syncSubmitBtn = () => {
        submitBtn.disabled = getSelectedItems().length === 0;
    };

    ticketList.addEventListener('click',  syncSubmitBtn);
    ticketList.addEventListener('input',  syncSubmitBtn);
    ticketList.addEventListener('change', syncSubmitBtn);

    ticketsForm.addEventListener('submit', async (e) => {
        e.preventDefault();
        setCheckoutError(null);

        const items = getSelectedItems();
        if (! items.length) return;

        submitBtn.disabled = true;
        submitBtn.textContent = 'Adding\u2026';

        try {
            const response = await fetch('/json/checkout/basket/add', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept':       'application/json',
                    'X-CSRF-TOKEN': window.csrfToken(),
                },
                body: JSON.stringify({
                    event_id:      parseInt(eventIdInput.value, 10),
                    event_date_id: parseInt(eventDateInput.value, 10),
                    items,
                }),
            });

            const json = await response.json();

            if (! response.ok || ! json.success) {
                setCheckoutError(json.error ?? 'Something went wrong. Please try again.');
                submitBtn.disabled = false;
                submitBtn.textContent = defaultSubmitText;
                return;
            }

            window.location.href = json.data.checkout_url;
        } catch {
            setCheckoutError('Something went wrong. Please try again.');
            submitBtn.disabled = false;
            submitBtn.textContent = defaultSubmitText;
        }
    });

    window.addEventListener('pageshow', (e) => {
        if (! e.persisted) return;
        submitBtn.textContent = defaultSubmitText;
        setCheckoutError(null);
        syncSubmitBtn();
    });

});
